<template>
  <CustomModal
    :title="modalText"
    :visible="visible"
    @update:visible="updateVisible"
    :width="'32vw'"
  >
    <div v-if="isGenerated" class="flex flex-col gap-6">
      <div class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">API Key</span>
        <div class="text-sm rounded-lg p-2 border_8 space_grotesk">
          {{ generatedKey }}
        </div>
      </div>

      <CustomAlerts
        type="info"
        source="api_keys"
        description="This key will be shown only once now. If you lose it, you will have to generate new key"
      />
    </div>

    <div v-else id="api_key_name" class="flex flex-col gap-1">
      <span class="text-[13px] secondary_color">API key name</span>
      <a-input
        v-model:value="apiKeyName"
        class="input_medium"
        placeholder="Enter API key name.."
      />
    </div>

    <template #footer>
      <a-button v-if="isGenerated" class="primary_medium" @click="copyKey">
        <ThemeImage
          class="size-4"
          :icon="imageNames.COPY_GREY"
          alt="copy_icon"
        />
        Copy API Key
      </a-button>

      <div v-else class="flex flex-row items-center gap-2">
        <a-button class="secondary_medium" @click="updateVisible(false)"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium"
          @click="toGenerateAPIKey"
          :loading="isGenerating"
          :disabled="!apiKeyName.trim()"
          >{{ isGenerating ? "Generating.." : "Generate API Key" }}</a-button
        >
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref } from "vue";
import { message } from "ant-design-vue";
import { copyToClipboard } from "@/src/utils/utils.js";

import CustomModal from "@/src/components/CustomModal.vue";
import CustomAlerts from "@/src/components/CustomAlerts.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  visible: Boolean,
});

const emit = defineEmits(["update:visible", "api-key-generated"]);

const isGenerating = ref(false);
const isGenerated = ref(false);
const generatedKey = ref("");
const apiKeyName = ref("");

const modalText = computed(() => {
  return isGenerated.value
    ? `'${apiKeyName.value}' API Key Generated`
    : "Add API Key";
});

const resetValues = () => {
  generatedKey.value = "";
  isGenerated.value = false;
  isGenerating.value = false;
  apiKeyName.value = "";
};

const updateVisible = (value) => {
  emit("update:visible", value);
  if (!value) {
    resetValues();
  }
};

const copyKey = () => {
  copyToClipboard(generatedKey.value);
};

const toGenerateAPIKey = async () => {
  try {
    if (!apiKeyName.value?.trim()) {
      message.warning("Please enter a name for your API key");
      return;
    }

    isGenerating.value = true;

    const payload = {
      name: apiKeyName.value?.trim(),
    };

    const response = await DashboardService.generateAPIKey(payload);
    if (response?.success) {
      isGenerated.value = true;
      generatedKey.value = response?.token;
      emit("api-key-generated");
      message.success("API Key successfully generated!");
    }
  } catch (error) {
    message.error("An error occurred while generating API Key");
    console.error("An error occurred while generating API Key: ", error);
  } finally {
    isGenerating.value = false;
  }
};
</script>
